import Card from '@/components/common/Card'
import FormField from '@/components/common/FormField'
import MainHeading from '@/components/common/MainHeading'
import Layout from '@/components/layout/Layout'
import { Button } from '@/components/ui/button'
import type { site, siteResponse } from '@/interface/site.interface'
import { SiteService } from '@/services/Site/SiteServive'
import { showToastNotification } from '@/utility/helper'
import { RoutesLink } from '@/utility/RouteLinks'
import { errorTypes } from '@/utility/validation'
import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import { ThrowError } from '@/api/callApi'
import SearchSelect from '@/components/common/SearchSelect'
import { BranchService } from '@/services/Branch/BranchSevice'
import type { branch } from '@/interface/branch.interface'
import SiteMapPicker from '@/components/common/SiteMapPicker'
import type { MapPickerResult } from '@/components/common/SiteMapPicker'
import FullPageLoader from '@/components/common/FullPageLoader'

const AddSite: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams()
    const [isLoading, setIsLoading] = useState(false);
    const [isFetching, setIsFetching] = useState(false);
    const [branches, setBranches] = useState<{ value: string; label: string }[]>([]);

    // ── Map result display state (source of truth for map-derived fields) ─────
    const [mapResult, setMapResult] = useState<MapPickerResult>({
        lat: 0,
        lng: 0,
        searchedLocation: '',
        address: '',
    });

    // ── Validation errors for map fields ──────────────────────────────────────
    const [mapErrors, setMapErrors] = useState({
        searchedLocation: '',
        address: '',
    });

    const { register, handleSubmit, setValue, watch, formState: { errors } } = useForm<site>({
        mode: "onChange",
    });
    const watchedRange = watch('range');

    // ── Branches ──────────────────────────────────────────────────────────────
    const getBranches = async () => {
        setIsFetching(true)
        try {
            const response = await BranchService.getBranch();
            if (response?.status) {
                const formatted = (response?.data?.branch || [])?.map((b: branch) => ({
                    value: b?.id || "",
                    label: b?.name || ""
                }));
                setBranches(formatted);
            }
        } catch (error) {
            ThrowError(error);
        } finally {
            setIsFetching(false)
        }
    }

    // ── Edit mode: pre-fill form ──────────────────────────────────────────────
    const getSiteById = async () => {
        if (!id) return;
        setIsFetching(true)
        try {
            const response = await SiteService.getSiteById(id);
            if (response?.status) {
                const data = response?.data;
                setValue("name", data?.name);
                setValue("type", data?.type);
                setValue("range", data?.range);
                setValue("status", data?.status);
                setValue("isPunchAllowed", data?.isPunchAllowed);
                setValue("branchId", data?.branchId);
                setValue("location", data?.location);

                setMapResult({
                    lat: data?.location?.lat ?? 0,
                    lng: data?.location?.lng ?? 0,
                    searchedLocation: data?.searchedLocation ?? '',
                    address: data?.address ?? '',
                });
            }
        } catch (error) {
            ThrowError(error);
        } finally {
            setIsFetching(false)
        }
    }

    useEffect(() => {
        getBranches();
        if (id) getSiteById();
    }, [id]);

    // ── Map picker callback ───────────────────────────────────────────────────
    const handleMapChange = (result: MapPickerResult) => {
        setMapResult(result);
        setValue('location.lat', result.lat);
        setValue('location.lng', result.lng);
        // Clear validation errors when user selects a location
        setMapErrors(prev => ({
            ...prev,
            searchedLocation: result.searchedLocation ? '' : prev.searchedLocation,
        }));
    };

    // ── Validate map fields before submit ─────────────────────────────────────
    const validateMapFields = (): boolean => {
        const hasLocation = Boolean(
            mapResult.searchedLocation &&
            mapResult.searchedLocation.trim().length > 0 &&
            mapResult.lat &&
            mapResult.lng &&
            mapResult.lat !== 0 &&
            mapResult.lng !== 0
        );
        const hasAddress = Boolean(mapResult.address && mapResult.address.trim().length > 0);

        const errors = {
            searchedLocation: hasLocation
                ? ''
                : 'Location search is required. Please search and select a location on the map.',
            address: hasAddress
                ? ''
                : 'Address is required.',
        };
        setMapErrors(errors);

        if (!hasLocation) {
            showToastNotification('danger', 'Location on map is required. Please search and select a location.');
            return false;
        }

        if (!hasAddress) {
            showToastNotification('danger', 'Address is required.');
            return false;
        }

        return true;
    };

    // ── Submit ────────────────────────────────────────────────────────────────
    const onSubmit = async (data: site) => {
        // Validate map fields first
        if (!validateMapFields()) return;

        try {
            setIsLoading(true);
            const payload: site = {
                name: data?.name,
                type: data?.type,
                searchedLocation: mapResult.searchedLocation,
                address: mapResult.address,
                location: {
                    lat: mapResult.lat,
                    lng: mapResult.lng,
                },
                range: data?.range,
                status: String(data?.status) === "true",
                isPunchAllowed: String(data?.isPunchAllowed) === "true",
                branchId: data?.branchId,
            }
            let response: siteResponse;
            if (id) {
                response = await SiteService.updateSite(payload, id);
            } else {
                response = await SiteService.createSite(payload);
            }

            if (response?.status) {
                showToastNotification("success", response?.message)
                navigate(RoutesLink.superAdminSite)
            } else {
                showToastNotification("danger", response?.message)
            }
        } catch (error) {
            ThrowError(error);
        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        register("branchId", { ...errorTypes.required("Branch") });
    }, [register]);

    if (isFetching) {
        return (
            <Layout>
                <FullPageLoader />
            </Layout>
        )
    }

    return (
        <Layout>
            <div className='space-y-6'>
                <MainHeading
                    title={id ? "Edit Site" : "Add Site"}
                    isBackButtonVisible
                    onClick={() => navigate(-1)}
                />
                <Card className='overflow-visible'>
                    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                            {/* Site Name */}
                            <FormField
                                {...register("name", { ...errorTypes.required('Name'), ...errorTypes?.name })}
                                type="text"
                                label="Site Name"
                                maxLength={50}
                                placeholder="Enter Site Name"
                                error={errors.name?.message}
                                required
                            />

                            {/* Type */}
                            <FormField
                                {...register("type", { ...errorTypes.required('Type'), ...errorTypes?.name })}
                                type="text"
                                label="Type"
                                maxLength={30}
                                placeholder="e.g. office, campus"
                                error={errors.type?.message}
                                required
                            />

                            {/* Branch */}
                            <SearchSelect
                                label="Branch"
                                required
                                placeholder="Select Branch"
                                options={branches}
                                value={watch("branchId")}
                                onChange={(val) => setValue("branchId", val)}
                                error={errors.branchId?.message}

                            />

                            {/* Is Punch Allowed */}
                            <FormField
                                {...register("isPunchAllowed", { ...errorTypes.required('Punch Allowed') })}
                                type="select"
                                value={String(watch("isPunchAllowed") ?? "")}
                                options={[
                                    { value: "select", label: "Select Option" },
                                    { value: "true", label: "Yes" },
                                    { value: "false", label: "No" },
                                ]}
                                label="Is Punch Allowed"
                                error={errors.isPunchAllowed?.message}
                                required
                            />

                            {/* Status */}
                            <FormField
                                {...register("status", { ...errorTypes.required('Status') })}
                                type="select"
                                value={String(watch("status") ?? "")}
                                options={[
                                    { value: "select", label: "Select Status" },
                                    { value: "true", label: "Active" },
                                    { value: "false", label: "Inactive" },
                                ]}
                                label="Status"
                                error={errors.status?.message}
                                required
                            />

                            {/* Range */}
                            <FormField
                                {...register("range", {
                                    ...errorTypes.required('Range'),
                                    ...errorTypes?.onlyNumbers,
                                    maxLength: {
                                        value: 4,
                                        message: "Range cannot exceed 4 digits (max 9999)",
                                    },
                                    max: {
                                        value: 9999,
                                        message: "Range cannot exceed 9999 meters",
                                    },
                                })}
                                type="number"
                                label="Range (meters)"
                                placeholder="e.g. 100"
                                min={0}
                                max={9999}
                                maxLength={4}
                                // onInput={(e: React.FormEvent<HTMLInputElement>) => {
                                //     if (e.currentTarget.value.length > 4) {
                                //         e.currentTarget.value = e.currentTarget.value.slice(0, 4);
                                //     }
                                // }}
                                error={errors.range?.message}
                                required
                            />

                            {/* ── Google Map Picker (full-width) ── */}
                            <div className="col-span-1 md:col-span-2 space-y-4">

                                {/* Map — passes external validation error to show on search box */}
                                <SiteMapPicker
                                    lat={mapResult.lat || undefined}
                                    lng={mapResult.lng || undefined}
                                    range={watchedRange}
                                    searchedLocation={mapResult.searchedLocation}
                                    searchedLocationError={mapErrors.searchedLocation}
                                    onChange={handleMapChange}
                                />

                                {/* Auto-filled / editable fields below the map */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                                    {/* Searched Location — read-only, filled by map */}
                                    <div className="space-y-1">
                                        <label className="block text-sm font-medium text-gray-700">
                                            Searched Location <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            readOnly
                                            value={mapResult.searchedLocation}
                                            placeholder="Auto-filled from map"
                                            className={`w-full h-10 px-3 rounded-lg border bg-gray-50 text-sm text-gray-700 cursor-default focus:outline-none transition-colors ${mapErrors.searchedLocation
                                                ? 'border-red-400'
                                                : 'border-input'
                                                }`}
                                        />
                                        {mapErrors.searchedLocation && (
                                            <p className="text-xs text-red-500 mt-0.5">{mapErrors.searchedLocation}</p>
                                        )}
                                    </div>

                                    {/* Address — editable manually + auto-filled from map */}
                                    <div className="space-y-1">
                                        <label className="block text-sm font-medium text-gray-700">
                                            Address <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            value={mapResult.address}
                                            placeholder="Auto-filled from map or type manually"
                                            onChange={(e) => {
                                                setMapResult(prev => ({ ...prev, address: e.target.value }));
                                                if (e.target.value.trim()) {
                                                    setMapErrors(prev => ({ ...prev, address: '' }));
                                                }
                                            }}
                                            className={`w-full h-10 px-3 rounded-lg border bg-white text-sm text-gray-700 focus:outline-none focus:ring-1 transition-colors ${mapErrors.address
                                                ? 'border-red-400 focus:ring-red-400'
                                                : 'border-input focus:ring-primary'
                                                }`}
                                        />
                                        {mapErrors.address && (
                                            <p className="text-xs text-red-500 mt-0.5">{mapErrors.address}</p>
                                        )}
                                    </div>

                                    {/* Latitude — read-only */}
                                    <div className="space-y-1">
                                        <label className="block text-sm font-medium text-gray-700">Latitude <span className="text-red-500">*</span></label>
                                        <input
                                            type="text"
                                            readOnly
                                            value={mapResult.lat ? mapResult.lat.toFixed(6) : ''}
                                            placeholder="Auto-filled from map"
                                            className="w-full h-10 px-3 rounded-lg border border-input bg-gray-50 text-sm text-gray-500 cursor-default focus:outline-none"

                                        />
                                    </div>

                                    {/* Longitude — read-only */}
                                    <div className="space-y-1">
                                        <label className="block text-sm font-medium text-gray-700">Longitude <span className="text-red-500">*</span></label>
                                        <input
                                            type="text"
                                            readOnly
                                            value={mapResult.lng ? mapResult.lng.toFixed(6) : ''}
                                            placeholder="Auto-filled from map"
                                            className="w-full h-10 px-3 rounded-lg border border-input bg-gray-50 text-sm text-gray-500 cursor-default focus:outline-none"

                                        />
                                    </div>
                                </div>
                            </div>

                        </div>

                        <div className="flex justify-end">
                            <Button disabled={isLoading} type="submit">
                                {isLoading ? "Submitting..." : "Submit"}
                            </Button>
                        </div>
                    </form>
                </Card>
            </div>
        </Layout>
    )
}

export default AddSite;
